/**
 * Secure User Profile Resolution Hooks
 *
 * React Query hooks for secure, cached profile name resolution via Cloud Function.
 * These hooks replace direct Firestore reads for cross-user profile lookups.
 *
 * SECURITY (Audit Jan 2026):
 * All cross-user profile resolution MUST go through these hooks.
 * The Cloud Function verifies that the caller shares at least one team with the target.
 *
 * Usage:
 * ```tsx
 * // Single user
 * const { displayName, isLoading } = useSecureUserProfile(userId);
 *
 * // Multiple users (batch)
 * const { profiles, isLoading } = useSecureUserProfiles(userIds);
 * ```
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  resolveUserProfile,
  resolveUserProfiles,
  clearProfileCache,
} from '@/services/profileResolver.service';

// =============================================================================
// Query Keys
// =============================================================================

export const secureProfileKeys = {
  all: ['secureProfile'] as const,
  single: (uid: string) => [...secureProfileKeys.all, 'single', uid] as const,
  batch: (uids: string[]) => [...secureProfileKeys.all, 'batch', uids.sort().join(',')] as const,
};

// =============================================================================
// Single User Hook
// =============================================================================

interface UseSecureUserProfileResult {
  displayName: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to securely resolve a single user's display name.
 *
 * @param uid - The user ID to resolve
 * @returns Object with displayName, isLoading, and isError
 *
 * @example
 * ```tsx
 * const { displayName, isLoading } = useSecureUserProfile(event.createdBy);
 * if (isLoading) return <Skeleton />;
 * return <span>{displayName || 'Onbekend'}</span>;
 * ```
 */
export function useSecureUserProfile(uid: string | null | undefined): UseSecureUserProfileResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: secureProfileKeys.single(uid || ''),
    queryFn: () => resolveUserProfile(uid!),
    enabled: !!uid,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches service cache)
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    displayName: data ?? null,
    isLoading: !!uid && isLoading,
    isError,
  };
}

// =============================================================================
// Batch Users Hook
// =============================================================================

interface UseSecureUserProfilesResult {
  profiles: Record<string, string | null>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to securely resolve multiple user display names in a batch.
 * Optimized for list views where many users need to be resolved.
 *
 * @param uids - Array of user IDs to resolve
 * @returns Object with profiles map, isLoading, and isError
 *
 * @example
 * ```tsx
 * const userIds = events.map(e => e.createdBy).filter(Boolean);
 * const { profiles, isLoading } = useSecureUserProfiles(userIds);
 *
 * return events.map(event => (
 *   <div key={event.id}>
 *     {profiles[event.createdBy] || 'Onbekend'}
 *   </div>
 * ));
 * ```
 */
export function useSecureUserProfiles(
  uids: (string | null | undefined)[]
): UseSecureUserProfilesResult {
  // Filter and deduplicate UIDs
  const validUids = useMemo(() => {
    const filtered = uids.filter((uid): uid is string => !!uid && uid.length > 0);
    return [...new Set(filtered)];
  }, [uids]);

  const { data, isLoading, isError } = useQuery({
    queryKey: secureProfileKeys.batch(validUids),
    queryFn: () => resolveUserProfiles(validUids),
    enabled: validUids.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    profiles: data ?? {},
    isLoading: validUids.length > 0 && isLoading,
    isError,
  };
}

// =============================================================================
// Display Name Hook (Convenience)
// =============================================================================

/**
 * Hook to get a display name with fallback.
 * Returns the resolved name or a fallback string.
 *
 * @param uid - The user ID to resolve
 * @param fallback - Fallback string if name unavailable (default: 'Onbekend')
 * @returns The display name or fallback
 *
 * @example
 * ```tsx
 * const name = useSecureDisplayName(event.createdBy);
 * return <span>Door: {name}</span>;
 * ```
 */
export function useSecureDisplayName(
  uid: string | null | undefined,
  fallback: string = 'Onbekend'
): string {
  const { displayName, isLoading } = useSecureUserProfile(uid);

  if (!uid) return fallback;
  if (isLoading) return '...';
  return displayName || fallback;
}

// =============================================================================
// Cache Management
// =============================================================================

/**
 * Hook to get cache management functions.
 * Useful for clearing cache on logout or team switch.
 */
export function useProfileCacheManager() {
  const queryClient = useQueryClient();

  return {
    /**
     * Clear all cached profiles (both service cache and React Query cache).
     * Call this on logout or team switch.
     */
    clearAll: () => {
      clearProfileCache();
      queryClient.removeQueries({ queryKey: secureProfileKeys.all });
    },

    /**
     * Invalidate and refetch all profile queries.
     */
    invalidateAll: () => {
      clearProfileCache();
      queryClient.invalidateQueries({ queryKey: secureProfileKeys.all });
    },
  };
}
